import { cssFont } from './fonts';

// Text is laid out once, here, and every painter uses the result: the stage, the thumbnails
// and the export draw the same lines at the same baselines, so what is downloaded breaks
// where the preview broke. Lines wrap at spaces (a word longer than the box breaks inside
// it); `\n` starts a paragraph. The box height is derived from the lines, never stored as
// truth: `layer.height` is only kept in step for readers that do not lay text out.
let measuring;
function context() {
  if (measuring !== undefined) return measuring;
  measuring = null;
  if (typeof document === 'undefined' || /jsdom/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')) return measuring;
  try { measuring = document.createElement('canvas').getContext('2d'); } catch { measuring = null; }
  return measuring;
}
export const nativeLetterSpacing = ctx => !!ctx && 'letterSpacing' in ctx;

// Width of a run as it is painted: native canvas letter spacing where supported, glyph by
// glyph otherwise (both add the spacing after every character, like CSS).
export function measureRun(text, font, spacing, size) {
  const ctx = context();
  if (!ctx) return [...text].length * size * 0.56 + spacing * [...text].length;
  ctx.font = font;
  if (nativeLetterSpacing(ctx)) {
    ctx.letterSpacing = `${spacing}px`;
    return ctx.measureText(text).width;
  }
  return ctx.measureText(text).width + spacing * [...text].length;
}

const metrics = new Map();
// The face's ascent and descent: CSS centers the glyphs in the line box with them, and so
// does the canvas painter, so an inline editor laid over the canvas lines up with it.
export function fontMetrics(font, size) {
  const key = font;
  if (metrics.has(key)) return metrics.get(key);
  const ctx = context();
  let value = { ascent: size * 0.92, descent: size * 0.24 };
  if (ctx) {
    ctx.font = font;
    const measured = ctx.measureText('Hgjy');
    if (Number.isFinite(measured.fontBoundingBoxAscent) && measured.fontBoundingBoxAscent > 0) value = { ascent: measured.fontBoundingBoxAscent, descent: measured.fontBoundingBoxDescent };
  }
  metrics.set(key, value);
  return value;
}

function breakWord(word, max, measure) {
  const chars = [...word];
  const parts = [];
  while (chars.length) {
    let low = 1;
    let high = chars.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (measure(chars.slice(0, mid).join('')) <= max) low = mid; else high = mid - 1;
    }
    parts.push(chars.splice(0, Math.max(1, low)).join(''));
  }
  return parts;
}

function wrapParagraph(paragraph, max, measure) {
  const tokens = paragraph.match(/\s+|\S+/g) || [''];
  const lines = [];
  let line = '';
  tokens.forEach(token => {
    if (/^\s/.test(token)) { if (line) line += token; return; }
    const candidate = line + token;
    if (!line.trim() || measure(candidate) <= max) line = candidate;
    else { lines.push(line.replace(/\s+$/, '')); line = token; }
    if (measure(line) > max && [...line].length > 1) {
      const parts = breakWord(line, max, measure);
      line = parts.pop();
      lines.push(...parts);
    }
  });
  lines.push(line.replace(/\s+$/, ''));
  return lines;
}

const cache = new Map();
const CACHE_LIMIT = 600;
// Measurements change once a face arrives: the editor clears the cache when fonts load.
export function invalidateTextLayouts() { cache.clear(); metrics.clear(); }

// The lines of a text layer in its box: each with its text, width, x offset (alignment),
// top and baseline, all in document pixels relative to the box.
export function layoutText(text, style, width) {
  const highlight = style.highlight;
  const key = [text, style.font_family, style.font_weight, style.italic, style.font_size, style.line_height, style.letter_spacing, style.align, style.transform,
    highlight ? `${highlight.mode}:${highlight.padding}` : '', width].join('\u0001');
  if (cache.has(key)) return cache.get(key);
  const size = style.font_size;
  const font = cssFont(style, size);
  const spacing = style.letter_spacing * size;
  const lineHeight = size * style.line_height;
  const padX = highlight ? highlight.padding : 0;
  const padY = highlight?.mode === 'box' ? highlight.padding : 0;
  const inner = Math.max(1, width - 2 * padX);
  const measure = run => measureRun(run, font, spacing, size);
  const content = style.transform === 'uppercase' ? text.toLocaleUpperCase() : text;
  const { ascent, descent } = fontMetrics(font, size);
  const lines = content.split('\n').flatMap(paragraph => wrapParagraph(paragraph, inner, measure)).map((run, index) => {
    const runWidth = run ? measure(run) : 0;
    const x = style.align === 'left' ? padX : style.align === 'right' ? width - padX - runWidth : (width - runWidth) / 2;
    const top = padY + index * lineHeight;
    return { text: run, width: runWidth, x, top, baseline: top + (lineHeight - (ascent + descent)) / 2 + ascent };
  });
  const layout = { font, size, spacing, lineHeight, padX, padY, inner, lines, ascent, descent, height: Math.max(1, lines.length) * lineHeight + 2 * padY };
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
  cache.set(key, layout);
  return layout;
}

export const layoutTextLayer = layer => layoutText(layer.text, layer.style, layer.width);
export const textHeight = layer => layoutTextLayer(layer).height;
